import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import { BadgeIcon, ProfileIcon, TeamsIcon, CompetencyIcon } from '../../constants/icons';
import { useQuickProfileSearch } from '../../hooks/useProfileSearch';
import { useTeamSearch } from '../../hooks/useTeams';
import { useBadges } from '../../hooks/useBadges';
import { useCompetencies } from '../../hooks/useCompetencies';
import { useDebounce } from '../../hooks/useDebounce';
import { Badge, Competency, Profile, Team } from '../../../../shared/types';

interface GlobalSearchDropdownProps {
    /** Width of the search field */
    width?: number | string;
    /** Placeholder text */
    placeholder?: string;
    /** Callback when navigating to full search page */
    onSearchNavigate?: () => void;
}

type GlobalSearchType = 'profile' | 'team' | 'badge' | 'competency';

interface GlobalSearchOption {
    id: string;
    type: GlobalSearchType;
    label: string;
    description?: string;
    avatarUrl?: string;
    to: string;
}

const filterByQuery = <T,>(items: T[], query: string, fields: Array<(item: T) => string | undefined>) => {
    const normalizedQuery = query.trim().toLowerCase();
    if (normalizedQuery.length < 2) return [] as T[];
    return items.filter((item) =>
        fields.some((field) => (field(item) || '').toLowerCase().includes(normalizedQuery))
    );
};

export const GlobalSearchDropdown = ({
    width = 340,
    placeholder = 'Search',
    onSearchNavigate
}: GlobalSearchDropdownProps) => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [inputValue, setInputValue] = useState('');
    const [open, setOpen] = useState(false);

    const debouncedSearch = useDebounce(inputValue, 300);
    const normalizedQuery = debouncedSearch.trim();

    const { data: profileData, isLoading: profilesLoading } = useQuickProfileSearch(
        slug!,
        debouncedSearch,
        open && normalizedQuery.length >= 2
    );

    const { data: teamData, isLoading: teamsLoading } = useTeamSearch(
        slug!,
        {
            page: 1,
            limit: 8,
            query: normalizedQuery || undefined,
            status: 'active'
        },
        open && normalizedQuery.length >= 2
    );

    const { badges, isLoading: badgesLoading } = useBadges(slug);
    const { competencies, isLoading: competenciesLoading } = useCompetencies(slug);

    const badgeMatches = useMemo(() =>
        filterByQuery<Badge>(badges, normalizedQuery, [
            (badge) => badge.name,
            (badge) => badge.description,
            (badge) => badge.category
        ]),
    [badges, normalizedQuery]
    );

    const competencyMatches = useMemo(() =>
        filterByQuery<Competency>(competencies, normalizedQuery, [
            (competency) => competency.name,
            (competency) => competency.description,
            (competency) => competency.category,
            (competency) => competency.labels?.join(' ')
        ]),
    [competencies, normalizedQuery]
    );

    const profileOptions = useMemo(() => {
        const profiles = profileData?.data || [];
        return profiles.slice(0, 6).map((profile: Profile): GlobalSearchOption => ({
            id: profile._id,
            type: 'profile',
            label: profile.displayName,
            description: profile.email,
            avatarUrl: profile.profilePicture,
            to: `/c/${slug}/profile/${profile.handle}`
        }));
    }, [profileData, slug]);

    const teamOptions = useMemo(() => {
        const teams = teamData?.data || [];
        return teams.slice(0, 6).map((team: Team): GlobalSearchOption => ({
            id: team._id,
            type: 'team',
            label: team.name,
            description: team.description,
            to: `/c/${slug}/teams/${team.slug}`
        }));
    }, [teamData, slug]);

    const badgeOptions = useMemo(() =>
        badgeMatches.slice(0, 6).map((badge): GlobalSearchOption => ({
            id: badge._id,
            type: 'badge',
            label: badge.name,
            description: badge.category,
            avatarUrl: badge.imageUrl,
            to: `/c/${slug}/badges/${badge._id}`
        })),
    [badgeMatches, slug]
    );

    const competencyOptions = useMemo(() =>
        competencyMatches.slice(0, 6).map((competency): GlobalSearchOption => ({
            id: competency._id,
            type: 'competency',
            label: competency.name,
            description: competency.category,
            to: `/c/${slug}/competencies/manage/${competency._id}`
        })),
    [competencyMatches, slug]
    );

    const options = useMemo(() => {
        if (normalizedQuery.length < 2) return [] as GlobalSearchOption[];
        return [
            ...profileOptions,
            ...teamOptions,
            ...badgeOptions,
            ...competencyOptions
        ];
    }, [profileOptions, teamOptions, badgeOptions, competencyOptions, normalizedQuery.length]);

    const isLoading = profilesLoading || teamsLoading || badgesLoading || competenciesLoading;

    const handleInputChange = useCallback((_event: React.SyntheticEvent, value: string) => {
        setInputValue(value);
    }, []);

    const handleOptionSelect = useCallback((_event: React.SyntheticEvent, option: GlobalSearchOption | null) => {
        if (option) {
            navigate(option.to);
            setInputValue('');
            setOpen(false);
            onSearchNavigate?.();
        }
    }, [navigate, onSearchNavigate]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && inputValue.trim()) {
            navigate(`/c/${slug}/search?q=${encodeURIComponent(inputValue.trim())}`);
            setInputValue('');
            setOpen(false);
            onSearchNavigate?.();
        }
    }, [inputValue, navigate, onSearchNavigate, slug]);

    const handleViewAll = useCallback(() => {
        navigate(`/c/${slug}/search?q=${encodeURIComponent(inputValue.trim())}`);
        setInputValue('');
        setOpen(false);
        onSearchNavigate?.();
    }, [inputValue, navigate, onSearchNavigate, slug]);

    const groupLabel = (type: GlobalSearchType) => {
        switch (type) {
            case 'profile':
                return 'People';
            case 'team':
                return 'Teams';
            case 'badge':
                return 'Badges';
            case 'competency':
                return 'Competencies';
            default:
                return 'Results';
        }
    };

    const renderIcon = (type: GlobalSearchType) => {
        switch (type) {
            case 'profile':
                return <ProfileIcon fontSize="small" />;
            case 'team':
                return <TeamsIcon fontSize="small" />;
            case 'badge':
                return <BadgeIcon fontSize="small" />;
            case 'competency':
                return <CompetencyIcon fontSize="small" />;
            default:
                return <SearchIcon fontSize="small" />;
        }
    };

    return (
        <Autocomplete<GlobalSearchOption, false, false, false>
            sx={{ width }}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onChange={handleOptionSelect}
            options={options}
            loading={isLoading}
            filterOptions={(x) => x}
            groupBy={(option) => groupLabel(option.type)}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id && option.type === value.type}
            noOptionsText={
                inputValue.length < 2
                    ? 'Type at least 2 characters to search'
                    : 'No results found'
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={placeholder}
                    size="small"
                    onKeyDown={handleKeyDown}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <>
                                {isLoading ? (
                                    <CircularProgress color="inherit" size={16} />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        )
                    }}
                />
            )}
            renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                    <Box
                        component="li"
                        key={key}
                        {...otherProps}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}
                    >
                        <Avatar
                            src={option.avatarUrl}
                            sx={{ width: 32, height: 32, bgcolor: option.avatarUrl ? 'transparent' : 'action.selected' }}
                        >
                            {renderIcon(option.type)}
                        </Avatar>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant="caption" color="text.primary" noWrap fontWeight={500}>
                                {option.label}
                            </Typography>
                            {option.description && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                    sx={{ display: 'block' }}
                                >
                                    {option.description}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            }}
            ListboxProps={{
                sx: { maxHeight: 380 }
            }}
            PaperComponent={({ children, ...props }) => (
                <Box {...props} sx={{ bgcolor: 'background.paper', boxShadow: 3, borderRadius: 1 }}>
                    {children}
                    {inputValue.trim() && (
                        <Box
                            sx={{
                                p: 1.5,
                                borderTop: 1,
                                borderColor: 'divider',
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                            onClick={handleViewAll}
                        >
                            <Typography
                                variant="body2"
                                color="primary"
                                align="center"
                            >
                                View all results →
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        />
    );
};

export default GlobalSearchDropdown;
